/**
 * The chrome around every cell: gutter, drag grip, selection rail, status.
 *
 * The drag is the interesting part. Evaluation runs in document order, so moving a
 * cell can change what it means -- a drag is a semantic edit disguised as a gesture.
 * The drop target is therefore checked *before* the release, and a drop that would
 * leave a symbol undefined is refused with the reason and an offer to bring the
 * definitions along.
 *
 * Keyboard parity is not optional (14.5): Alt+Up / Alt+Down move the selected cell
 * under exactly the same rule, so reordering never requires a pointer.
 */
import { useRef, type KeyboardEvent, type ReactNode } from 'react'
import { GripVertical, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/cn'
import { CellStatusBadge } from './CellStatusBadge'
import type { CellId, CellResult } from '../model/types'

export interface CellRowProps {
  readonly id: CellId
  readonly index: number
  readonly result: CellResult | undefined
  readonly selected: boolean
  readonly shadowsCell: number | null
  readonly children: ReactNode
  readonly onSelect: () => void
  readonly onDelete: () => void
  readonly onMoveBy: (delta: number) => void
  readonly onDragStart: () => void
  readonly onDragOver: (position: 'before' | 'after') => void
  readonly onDrop: () => void
  readonly dropHint: 'none' | 'allowed' | 'blocked'
}

export function CellRow({
  id,
  index,
  result,
  selected,
  shadowsCell,
  children,
  onSelect,
  onDelete,
  onMoveBy,
  onDragStart,
  onDragOver,
  onDrop,
  dropHint,
}: CellRowProps) {
  const { t } = useTranslation('sheets')
  const row = useRef<HTMLDivElement | null>(null)

  const isError = result?.status === 'error'

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (!event.altKey) return
    if (event.key === 'ArrowUp') {
      event.preventDefault()
      onMoveBy(-1)
    } else if (event.key === 'ArrowDown') {
      event.preventDefault()
      onMoveBy(1)
    }
  }

  return (
    <div
      ref={row}
      data-cell-id={id}
      data-testid="sheet-cell"
      className={cn(
        'group relative rounded-e-md border-s-[3px] transition-colors',
        selected && !isError && 'border-s-accent bg-[var(--accent-subtle)]',
        isError && 'border-s-[var(--status-warn)] bg-[var(--status-warn-bg)]',
        !selected && !isError && 'border-s-transparent',
        dropHint === 'allowed' && 'shadow-[0_-2px_0_0_var(--accent)]',
        dropHint === 'blocked' && 'shadow-[0_-2px_0_0_var(--status-warn)]',
      )}
      onDragOver={(event) => {
        event.preventDefault()
        const box = row.current?.getBoundingClientRect()
        const half = box ? event.clientY < box.top + box.height / 2 : true
        onDragOver(half ? 'before' : 'after')
      }}
      onDrop={(event) => {
        event.preventDefault()
        onDrop()
      }}
    >
      <div className="flex items-start gap-3 px-3 py-1.5 ps-2">
        <div className="flex shrink-0 items-center gap-1 pt-1.5">
          {/* The grip is the interactive element, so it carries the keyboard
              equivalent of the drag: Alt+Up / Alt+Down move the cell under the same
              legality rule, and reordering never requires a pointer (14.5). */}
          <button
            type="button"
            draggable
            onDragStart={onDragStart}
            onKeyDown={handleKeyDown}
            onFocus={onSelect}
            aria-label={t('sheets.action.move_up')}
            className="cursor-grab text-text-muted opacity-30 transition-opacity hover:opacity-100 focus-visible:opacity-100 active:cursor-grabbing"
          >
            <GripVertical className="h-3.5 w-4" aria-hidden />
          </button>
          <span className="tabular w-5 text-end font-mono text-[11px] text-text-muted">
            {index + 1}
          </span>
        </div>

        {/* Selection follows focus rather than a click handler. Every cell body
            contains a real control -- a math field, a textarea, an input -- so
            clicking one focuses it and selects the row, without hanging mouse
            listeners off a presentational container. */}
        <div onFocusCapture={onSelect} className="min-w-0 flex-1">
          {children}
        </div>

        <div className="flex shrink-0 items-center gap-2 pt-1">
          {result && <CellStatusBadge status={result.status} errorKind={result.error?.kind} />}
          <button
            type="button"
            onClick={onDelete}
            aria-label={t('sheets.action.delete_cell')}
            className="text-text-muted opacity-0 transition-opacity hover:text-[var(--status-fail)] focus-visible:opacity-100 group-hover:opacity-100"
          >
            <Trash2 className="h-3.5 w-3.5" aria-hidden />
          </button>
        </div>
      </div>

      {shadowsCell !== null && (
        <p className="ps-12 pb-1.5 pe-3 text-xs text-[var(--status-warn)]">
          {t('sheets.cell.shadow_note', { cell: shadowsCell })}
        </p>
      )}
    </div>
  )
}
